// src/lib/jobFinancials.js
// Pure financial helper module — no React, no Supabase, no side effects.
// Source of truth for all job and invoice financial calculations in Field Canopy.
//
// Key rules:
//  - Always use getInvoiceTotal(inv) — never read inv.total or inv.amount directly (FA-008)
//  - Do not use invoices.amount_paid — deprecated, zero rows (FA-008)
//  - Unapplied payments (invoice_id = null) count toward job balance, NOT invoice balance (Session 4A)
//  - invoice_payments.invoice_id FK is ON DELETE SET NULL (FA-007)
//
// See Docs/DECISIONS_LOG.md FA-007, FA-008 for design decisions.

// ── Core numeric helpers ──────────────────────────────────────────────────────

/**
 * Safely convert any value to a financial number.
 * Returns 0 for null, undefined, NaN, or non-numeric values.
 */
export function toMoneyNumber(value) {
  const n = Number(value)
  return isNaN(n) ? 0 : n
}

/**
 * Get invoice total safely from either invoices.total or invoices.amount.
 * Both columns exist in the DB. Always call this — never read either directly.
 * Rule: FA-008
 */
export function getInvoiceTotal(invoice) {
  return toMoneyNumber(invoice?.total ?? invoice?.amount)
}

// ── Payment filtering ─────────────────────────────────────────────────────────

/**
 * Sum all payment amounts in an array.
 */
export function sumPayments(payments) {
  return (payments || []).reduce((s, p) => s + toMoneyNumber(p.amount), 0)
}

/**
 * Get all payments applied to a specific invoice (invoice_id matches).
 * Does NOT include unapplied payments (invoice_id = null).
 */
export function getAppliedPaymentsForInvoice(invoiceId, allPayments) {
  if (!invoiceId) return []
  return (allPayments || []).filter(p => p.invoice_id === invoiceId)
}

/**
 * Get all unapplied payments (invoice_id is null or undefined).
 *
 * These count toward job-level balance but NOT toward any invoice balance.
 * Rule locked Session 4A: unapplied payments DO count toward total job collected;
 * DO NOT count toward any specific invoice balance until applied.
 */
export function getUnappliedPayments(allPayments) {
  return (allPayments || []).filter(p => p.invoice_id === null || p.invoice_id === undefined)
}

/**
 * Group all payments by invoice_id.
 * Unapplied payments (null invoice_id) are stored under key '__unapplied__'.
 * Returns: { [invoiceId]: payment[], __unapplied__: payment[] }
 */
export function groupPaymentsByInvoice(allPayments) {
  return (allPayments || []).reduce((acc, p) => {
    const key = p.invoice_id || '__unapplied__'
    if (!acc[key]) acc[key] = []
    acc[key].push(p)
    return acc
  }, {})
}

// ── Invoice financials ────────────────────────────────────────────────────────

/**
 * Derive financial state for a single invoice.
 * Applied payments are only those with invoice_id matching this invoice.
 * Unapplied payments never affect invoice balance.
 *
 * Returns: { invoiceTotal, appliedTotal, balance, isOverpaid }
 */
export function deriveInvoiceFinancials(invoice, allPayments) {
  const invoiceTotal = getInvoiceTotal(invoice)
  const applied = getAppliedPaymentsForInvoice(invoice?.id, allPayments)
  const appliedTotal = sumPayments(applied)
  const balance = invoiceTotal - appliedTotal
  const isOverpaid = appliedTotal > invoiceTotal && invoiceTotal > 0
  return { invoiceTotal, appliedTotal, balance, isOverpaid }
}

/**
 * Derive the correct status string for an invoice from its payments and due date.
 *
 * Rules:
 *  - 'void' and 'cancelled' are always preserved (lifecycle states, not financial)
 *  - If invoiceTotal <= 0, preserve current status (no financial basis to derive)
 *  - appliedTotal >= invoiceTotal and total > 0 → 'paid'
 *  - appliedTotal > 0 and < invoiceTotal → 'partial'
 *  - due_date < today AND not draft AND balance > 0 → 'overdue'
 *    (due TODAY is NOT overdue — only strictly less than today)
 *  - draft stays 'draft' if no payments; else → 'sent' (or preserve sent)
 *
 * @param {object} invoice  - invoice row with status, due_date, total, amount
 * @param {number} appliedTotal - sum of payments applied to this invoice
 * @returns {string} derived status
 */
export function deriveInvoiceStatus(invoice, appliedTotal) {
  // Always preserve lifecycle statuses — user decisions, not financial derivations
  if (invoice?.status === 'void' || invoice?.status === 'cancelled') {
    return invoice.status
  }

  const invoiceTotal = getInvoiceTotal(invoice)

  // No financial basis to derive a status
  if (invoiceTotal <= 0) {
    return invoice?.status || 'draft'
  }

  const balance = invoiceTotal - appliedTotal

  if (appliedTotal >= invoiceTotal) return 'paid'
  if (appliedTotal > 0) return 'partial'

  // No payments — check overdue (date-only, strict less-than)
  const today = new Date().toISOString().slice(0, 10)
  const isDraft = invoice?.status === 'draft'
  if (invoice?.due_date && invoice.due_date < today && !isDraft && balance > 0) {
    return 'overdue'
  }

  return isDraft ? 'draft' : (invoice?.status || 'sent')
}

// ── Job financials ────────────────────────────────────────────────────────────

/**
 * Derive effective contract value for a job.
 * = base contract + change-order invoice totals (excluding void/cancelled).
 * Uses getInvoiceTotal() for all change-order amounts.
 */
function deriveEffectiveContract(job, invoices) {
  const baseContract = toMoneyNumber(job?.contract_amount)
  const changeOrderTotal = (invoices || [])
    .filter(i =>
      i.invoice_type === 'change_order' &&
      i.status !== 'void' &&
      i.status !== 'cancelled'
    )
    .reduce((s, i) => s + getInvoiceTotal(i), 0)
  return baseContract + changeOrderTotal
}

/**
 * Derive full financial state for a job.
 *
 * ALL invoice_payments for the job are included in totalCollected —
 * including unapplied payments (invoice_id = null). This is the locked rule.
 *
 * Returns:
 * {
 *   baseContract,
 *   effectiveContract,
 *   changeOrderTotal,
 *   totalCollected,
 *   totalUnapplied,
 *   unappliedPayments,
 *   remainingBalance,
 *   creditBalance,
 *   isOverpaid,
 *   isMissingContract,
 *   paymentStatus,
 *   invoiceFinancials: { [invoiceId]: { invoiceTotal, appliedTotal, balance, isOverpaid } }
 * }
 */
export function deriveJobFinancials(job, invoices, allPayments) {
  const baseContract = toMoneyNumber(job?.contract_amount)
  const effectiveContract = deriveEffectiveContract(job, invoices)
  const changeOrderTotal = effectiveContract - baseContract

  const totalCollected = sumPayments(allPayments)

  const unapplied = getUnappliedPayments(allPayments)
  const totalUnapplied = sumPayments(unapplied)

  const remainingBalance = Math.max(0, effectiveContract - totalCollected)
  const creditBalance = Math.max(0, totalCollected - effectiveContract)
  const isOverpaid = totalCollected > effectiveContract && effectiveContract > 0
  const isMissingContract = !job?.contract_amount || baseContract <= 0

  const paymentStatus = deriveJobPaymentStatus(totalCollected, effectiveContract)

  const invoiceFinancials = {}
  for (const inv of (invoices || [])) {
    invoiceFinancials[inv.id] = deriveInvoiceFinancials(inv, allPayments)
  }

  return {
    baseContract,
    effectiveContract,
    changeOrderTotal,
    totalCollected,
    totalUnapplied,
    unappliedPayments: unapplied,
    remainingBalance,
    creditBalance,
    isOverpaid,
    isMissingContract,
    paymentStatus,
    invoiceFinancials,
  }
}

/**
 * Derive jobs.payment_status from collected vs effective contract.
 *
 * Rules:
 *  contract <= 0 and collected <= 0 → 'unpaid'
 *  contract <= 0 and collected > 0  → 'partial' (missing contract + payment warning applies)
 *  collected <= 0                   → 'unpaid'
 *  collected > contract             → 'overpaid'
 *  collected >= contract            → 'paid'
 *  otherwise                        → 'partial'
 */
export function deriveJobPaymentStatus(totalCollected, effectiveContract) {
  const collected = toMoneyNumber(totalCollected)
  const contract = toMoneyNumber(effectiveContract)

  if (contract <= 0 && collected <= 0) return 'unpaid'
  if (contract <= 0 && collected > 0) return 'partial'
  if (collected <= 0) return 'unpaid'
  if (collected > contract) return 'overpaid'
  if (collected >= contract) return 'paid'
  return 'partial'
}

// ── Alert derivation ──────────────────────────────────────────────────────────

/**
 * Derive financial alerts for a job from its current financial state.
 *
 * Alert types:
 *  'missing_contract'         — no contract amount set, no payments
 *  'zero_contract_with_payment' — $0 contract but payments exist
 *  'unapplied_payment'        — payments not linked to any invoice
 *  'overpayment'              — collected exceeds contract
 *
 * severity: 'error' | 'warning'
 * action: hint for what UI action resolves this alert
 */
export function deriveFinancialAlerts(job, jobFinancials) {
  const alerts = []
  const {
    totalCollected,
    totalUnapplied,
    unappliedPayments,
    isOverpaid,
    creditBalance,
    isMissingContract,
  } = jobFinancials

  if (isMissingContract && !job?.is_internal) {
    if (totalCollected > 0) {
      alerts.push({
        type: 'zero_contract_with_payment',
        message: `${formatCurrency(totalCollected)} received but no contract amount is set. Job cannot be marked paid.`,
        severity: 'error',
        action: 'edit_contract',
      })
    } else {
      alerts.push({
        type: 'missing_contract',
        message: 'No contract amount set. Add a contract amount to track job balance.',
        severity: 'warning',
        action: 'edit_contract',
      })
    }
  }

  if (unappliedPayments?.length > 0) {
    alerts.push({
      type: 'unapplied_payment',
      message: `${formatCurrency(totalUnapplied)} received but not applied to any invoice.`,
      severity: 'warning',
      action: 'apply_payment',
      count: unappliedPayments.length,
    })
  }

  if (isOverpaid) {
    alerts.push({
      type: 'overpayment',
      message: `${formatCurrency(creditBalance)} credit — more has been collected than the contract total.`,
      severity: 'warning',
      action: 'review_payments',
    })
  }

  return alerts
}

// Closes half of TD-016 — was private (`_fmt`), duplicated inline as slightly
// different variants across Estimates.js/Invoices.js/Proposals.js. Same output
// as the existing per-page `fmt$` pattern in Jobs.js/Dashboard.js — not touching
// those, this is for pages that had no shared helper at all.
export function formatCurrency(v) {
  return `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}
